import React, { useState, useEffect } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import PasswordField from './passwordField';
import AdminApi from '../api/adminApi';
import AuthService from '../utils/authService';
import { validatePassword } from '../utils/passwordRules';
import PasswordRulesHint from './passwordRulesHint';

// CHECKPOINT-75: the forced "Change Your Password" screen.
// Shown (by app.js) whenever the logged-in user's token carries must_change_password.
// It is rendered INSTEAD of the app shell — not behind a redirect — so the user can
// never be bounced into a loop. It is always submittable and always offers logout.
const apiError = (err, fallback) => {
    const data = err && err.response && err.response.data;
    if (typeof data === 'string' && data) return data;
    if (data && data.message) return data.message;
    return fallback;
};

const ChangePasswordPage = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [rules, setRules] = useState(null);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let active = true;
        AdminApi.getPasswordRules()
            .then(res => { if (active) setRules(res.data || null); })
            .catch(() => { /* rules are a nicety; the server still validates */ });
        return () => { active = false; };
    }, []);

    const clearError = () => { if (error) setError(''); };

    const submit = (e) => {
        if (e) e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError('The new password and confirmation do not match.');
            return;
        }
        const problems = validatePassword(newPassword, rules);
        if (problems.length) {
            setError(problems.join(' '));
            return;
        }
        setSubmitting(true);
        AdminApi.changeOwnPassword({
            newPassword,
            confirmPassword,
            client: AuthService.getClient()
        })
            .then(() => {
                // Success: mint a fresh token (without the must-change flag) by
                // re-logging in, so the flag is cleared everywhere and the app opens.
                AuthService.login(AuthService.getUserName(), newPassword, AuthService.getClient())
                    .then(() => { window.location.href = '/'; })
                    .catch(() => {
                        // Password changed, but auto re-login failed — send them to a
                        // clean login rather than leaving them stranded.
                        AuthService.logout().finally(() => { window.location.href = '/'; });
                    });
            })
            .catch(err => {
                setError(apiError(err, 'Could not change your password. Please try again.'));
                setSubmitting(false);
            });
    };

    const logout = () => {
        AuthService.logout().finally(() => { window.location.href = '/'; });
    };

    return (
        <div className="login">
            <div className="login-container">
                <div className="panel panel-default">
                    <div className="panel-heading">
                        <img src="/assets/Images/Logo_2.png" alt="" />
                        <span>Change your password to continue</span>
                        <div className="login-heading-divider"></div>
                    </div>
                    <div className="panel-body">
                        <form onSubmit={submit}>
                            <div className="form-login">
                                <PasswordField
                                    value={newPassword}
                                    type="password"
                                    name="newPassword"
                                    onChange={(ev) => { setNewPassword(ev.target.value); clearError(); }}
                                    label="New Password"
                                />
                                <PasswordField
                                    value={confirmPassword}
                                    type="password"
                                    name="confirmPassword"
                                    error={error}
                                    onChange={(ev) => { setConfirmPassword(ev.target.value); clearError(); }}
                                    label="Confirm New Password"
                                />

                                <PasswordRulesHint rules={rules} password={newPassword} />

                                <div className="top-40">
                                    <Button
                                        type="button"
                                        style={{ width: '100%', height: 50, fontSize: '16px', borderRadius: '8px' }}
                                        themeColor={'primary'}
                                        disabled={submitting}
                                        onClick={submit}
                                    >
                                        <span className="mdi mdi-lock-check" style={{ marginRight: 8 }}></span>
                                        {submitting ? 'Updating…' : 'Change Password'}
                                    </Button>
                                </div>

                                <div className="top-40" style={{ textAlign: 'center' }}>
                                    <a className="login-forgot-link" href="#" onClick={(ev) => { ev.preventDefault(); logout(); }}>
                                        Log out
                                    </a>
                                </div>

                                <div className="login-footer">&copy; Checkpoint 2026</div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordPage;
